import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Avatar from '@mui/material/Avatar'
import Paper from '@mui/material/Paper'
import LinearProgress from '@mui/material/LinearProgress'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import VerifiedIcon from '@mui/icons-material/Verified'
import PersonIcon from '@mui/icons-material/Person'
import Wrapper from '../components/Wrapper'
import {
  getPublicProfile,
  PublicProfileData,
  TickBreakdown
} from '../services/profileStatsService'

const TICK_LABELS: { key: keyof TickBreakdown; label: string }[] = [
  { key: 'showedUp', label: 'Showed up' },
  { key: 'divedResponsibly', label: 'Dived responsibly' },
  { key: 'knewGearSetup', label: 'Knew how to set up gear' },
  { key: 'goodBuddy', label: 'Good buddy' },
  { key: 'wouldDiveAgain', label: 'Would dive again' }
]

const PublicProfile = () => {
  const { userId } = useParams<{ userId: string }>()
  const [profileData, setProfileData] = useState<PublicProfileData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) {
      setError('User ID is missing')
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    getPublicProfile(userId)
      .then(data => {
        if (cancelled) return
        if (!data) {
          setError('Diver not found')
        } else {
          setProfileData(data)
        }
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load profile')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [userId])

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh'
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error || !profileData) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity='error'>{error || 'Diver not found'}</Alert>
      </Box>
    )
  }

  const { totalRatings, tickBreakdown, ratingAggregates } = profileData
  const initials = profileData.displayName
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <Wrapper>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, mt: 1 }}>
        <Avatar sx={{ width: 64, height: 64, fontSize: 24 }}>
          {initials || <PersonIcon />}
        </Avatar>
        <Box>
          <Typography variant='h5' fontWeight='bold'>
            {profileData.displayName}
          </Typography>
          {profileData.handle && (
            <Typography variant='body2' color='text.secondary'>
              @{profileData.handle}
            </Typography>
          )}
          {profileData.suburb && (
            <Typography variant='caption' color='text.secondary'>
              {profileData.suburb}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Accreditation */}
      {profileData.accreditationVerified && profileData.accreditationType && (
        <Chip
          icon={<VerifiedIcon />}
          label={`${profileData.accreditationType.toUpperCase()}${
            profileData.accreditationLevel
              ? ` — ${profileData.accreditationLevel}`
              : ''
          }`}
          color='success'
          variant='outlined'
          sx={{ mb: 3 }}
        />
      )}

      {/* Rating Summary */}
      <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
        {totalRatings === 0 ? (
          <Typography variant='body1' color='text.secondary'>
            No ratings yet
          </Typography>
        ) : (
          <>
            <Stack
              direction='row'
              alignItems='baseline'
              spacing={1}
              sx={{ mb: 1 }}
            >
              <Typography variant='h4' fontWeight='bold'>
                {ratingAggregates.averageTickScore.toFixed(1)}
              </Typography>
              <Typography variant='body1' color='text.secondary'>
                / 5 ticks
              </Typography>
            </Stack>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              from {totalRatings} rating{totalRatings !== 1 ? 's' : ''}
            </Typography>

            {/* Tick breakdown */}
            <Stack spacing={1.5}>
              {TICK_LABELS.map(({ key, label }) => {
                const pct =
                  totalRatings > 0
                    ? Math.round((tickBreakdown[key] / totalRatings) * 100)
                    : 0
                return (
                  <Box key={key}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.5
                      }}
                    >
                      <Typography variant='body2'>{label}</Typography>
                      <Typography variant='body2' fontWeight='bold'>
                        {pct}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant='determinate'
                      value={pct}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': { borderRadius: 4 }
                      }}
                    />
                  </Box>
                )
              })}
            </Stack>
          </>
        )}
      </Paper>
    </Wrapper>
  )
}

export default PublicProfile
